import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle2, XCircle, Send, Unplug, ArrowRight, AlertTriangle, Ban, FileSearch, KeyRound, ListChecks, Lock, LogOut, ShieldAlert, ShieldCheck, Timer, UserCog, UserMinus, UserPlus, UserX, Users } from 'lucide-react';
import { apiClient } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { ReactComponent as OktaIcon } from '../assets/icons/okta-mono.svg';

const OktaIntegration: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [exampleUser, setExampleUser] = useState('');
  const [exampleChannel, setExampleChannel] = useState('');

  const [orgUrl, setOrgUrl] = useState('');
  const [lastPollAt, setLastPollAt] = useState<string | null>(null);
  const [lastEventAt, setLastEventAt] = useState<string | null>(null);

  const [connectOrgUrl, setConnectOrgUrl] = useState('');
  const [connectApiToken, setConnectApiToken] = useState('');

  const fetchStatus = async () => {
    try {
      const data = await apiClient.getOktaStatus();
      if (data?.connected) {
        setConnected(true);
        setOrgUrl(data.org_url || '');
        setLastPollAt(data.last_poll_at || null);
        setLastEventAt(data.last_event_at || null);
      } else {
        setConnected(false);
      }
    } catch {
      setConnected(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleConnect = async () => {
    setError(null);
    setSuccessMsg(null);
    setSaving(true);
    try {
      await apiClient.connectOkta({
        org_url: connectOrgUrl.trim(),
        api_token: connectApiToken,
      });
      setSuccessMsg('Okta connected successfully!');
      setConnectApiToken('');
      await fetchStatus();
    } catch (err: any) {
      setError(err.response?.data || err.message || 'Failed to connect');
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    setError(null);
    setSuccessMsg(null);
    try {
      await apiClient.disconnectOkta();
      setConnected(false);
      setSuccessMsg('Okta disconnected.');
    } catch (err: any) {
      setError(err.response?.data || 'Failed to disconnect');
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const data = await apiClient.testOkta();
      if (data?.success) {
        setTestResult({ success: true, message: `Connected to Okta (${data.user_count ?? 0} user${data.user_count !== 1 ? 's' : ''} accessible)` });
      } else {
        setTestResult({ success: false, message: data?.error || 'Connection test failed' });
      }
    } catch (err: any) {
      setTestResult({ success: false, message: err.response?.data || 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {successMsg && (
        <Alert className="border-green-500 bg-green-50 dark:bg-green-950/30">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700 dark:text-green-400">{successMsg}</AlertDescription>
        </Alert>
      )}
      {connected ? (
        <>
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <OktaIcon className="w-7 h-7 text-foreground" />
              <div>
                <CardTitle className="flex items-center gap-2">
                  Okta
                  <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-normal">Connected</Badge>
                </CardTitle>
                <CardDescription>React to identity events (lockouts, MFA changes, privilege grants) and automate user and group response actions</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              {orgUrl && (
                <div className="min-w-0">
                  <span className="text-muted-foreground">Org URL</span>
                  <p className="font-medium truncate">{orgUrl}</p>
                </div>
              )}
              {lastPollAt && (
                <div>
                  <span className="text-muted-foreground">Last System Log Poll</span>
                  <p className="font-medium">{new Date(lastPollAt).toLocaleString()}</p>
                </div>
              )}
              {lastEventAt && (
                <div>
                  <span className="text-muted-foreground">Last Event</span>
                  <p className="font-medium">{new Date(lastEventAt).toLocaleString()}</p>
                </div>
              )}
            </div>

            {testResult && (
              <Alert variant={testResult.success ? 'default' : 'destructive'} className={testResult.success ? 'border-green-500 bg-green-50 dark:bg-green-950/30' : ''}>
                {testResult.success ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <XCircle className="h-4 w-4" />}
                <AlertDescription className={testResult.success ? 'text-green-700 dark:text-green-400' : ''}>{testResult.message}</AlertDescription>
              </Alert>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleTest} disabled={testing} className="gap-1">
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Test Connection
              </Button>
              <Button variant="outline" size="sm" onClick={handleDisconnect} className="gap-1">
                <Unplug className="w-3.5 h-3.5" /> Disconnect
              </Button>
            </div>

            <p className="text-xs text-muted-foreground">
              Kestrel tails your Okta System Log every minute (no webhook setup needed) and only requests the event types your active triggers subscribe to.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Available Workflow Blocks</CardTitle>
            <CardDescription>Use these Okta triggers and actions in your workflows to automate identity threat detection and response.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 mb-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Triggers (System Log poll)</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: 'User Locked Out', desc: 'Triggers when a user account is locked after failed sign-in attempts.', Icon: Lock },
                  { name: 'Suspicious Activity Reported', desc: 'Triggers when a user reports suspicious activity or Okta detects a threat.', Icon: ShieldAlert },
                  { name: 'Admin Privilege Granted', desc: 'Triggers when someone is granted an admin role.', Icon: ShieldCheck },
                  { name: 'MFA Factor Changed', desc: 'Triggers when an MFA factor is deactivated or all factors are reset.', Icon: KeyRound },
                  { name: 'User Created', desc: 'Triggers when a new user is created in Okta.', Icon: UserPlus },
                  { name: 'User Deactivated / Suspended', desc: 'Triggers on user deactivation or suspension.', Icon: UserX },
                  { name: 'Group Membership Changed', desc: 'Triggers when a user is added to or removed from a group.', Icon: Users },
                  { name: 'App Assignment Changed', desc: 'Triggers when a user is assigned to or removed from an application.', Icon: AlertTriangle },
                ].map(block => (
                  <div key={block.name} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <block.Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm min-w-0 truncate">{block.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{block.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: 'Get User', desc: 'Look up a user\u2019s profile, status, and last sign-in.', Icon: UserCog },
                  { name: 'List Users', desc: 'Search users by name/email and filter by status.', Icon: Users },
                  { name: 'Query System Log', desc: 'Pull recent audit events by event type or free-text search.', Icon: FileSearch },
                  { name: 'List User\u2019s Groups', desc: 'List every group a user belongs to.', Icon: ListChecks },
                  { name: 'List Group Members', desc: 'List the members of a group (e.g. an admin group).', Icon: Users },
                  { name: 'Suspend / Unsuspend User', desc: 'Temporarily block or restore a user\u2019s access.', Icon: Ban },
                  { name: 'Unlock User', desc: 'Unlock a user locked out by failed sign-in attempts.', Icon: Lock },
                  { name: 'Deactivate User', desc: 'Fully deactivate a user (offboarding / compromise response).', Icon: UserX },
                  { name: 'Clear User Sessions', desc: 'Revoke all active sessions, optionally OAuth tokens too.', Icon: LogOut },
                  { name: 'Expire Password', desc: 'Force a password reset at next sign-in.', Icon: Timer },
                  { name: 'Reset MFA Factors', desc: 'Reset enrolled MFA factors so the user must re-enroll.', Icon: KeyRound },
                  { name: 'Add / Remove Group Member', desc: 'Change group membership to grant or revoke access.', Icon: UserMinus },
                ].map(block => (
                  <div key={block.name} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <block.Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm min-w-0 truncate">{block.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{block.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Example Workflow</CardTitle>
            <CardDescription>Click the highlighted fields below to customize, then create the workflow.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground leading-loose">
              When{' '}
              <input
                value={exampleUser}
                onChange={e => setExampleUser(e.target.value)}
                placeholder="a user"
                style={{ width: `${Math.max(exampleUser.length || 6, 6)}ch` }}
                className="inline-block bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100 font-medium border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder:text-cyan-600 dark:placeholder:text-cyan-300"
              />{' '}reports suspicious activity in Okta, clear their sessions, expire their password, and post the details to{' '}
              <input
                value={exampleChannel}
                onChange={e => setExampleChannel(e.target.value)}
                placeholder="#channel"
                style={{ width: `${Math.max(exampleChannel.length || 8, 8)}ch` }}
                className="inline-block bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100 font-medium border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder:text-cyan-600 dark:placeholder:text-cyan-300"
              />{' '}on Slack.
            </div>
            <Button
              size="sm"
              className="gap-1.5"
              disabled={!exampleUser || !exampleChannel}
              onClick={() => navigate('/workflows/new', {
                state: {
                  suggestion: {
                    nl_prompt: `When ${exampleUser} reports suspicious activity in Okta, clear their Okta sessions, expire their password, and post the details to ${exampleChannel} on Slack.`,
                  },
                },
              })}
            >
              Create This Workflow <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </CardContent>
        </Card>
        </>
      ) : (
        <>
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <OktaIcon className="w-7 h-7 text-foreground" />
              <div>
                <CardTitle>Okta</CardTitle>
                <CardDescription>Connect Okta to react to identity security events and automate user lockout, session revocation, MFA resets, and group membership in workflows.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="okta-org-url" className="text-sm">Org URL</Label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">
                Your Okta organization URL — e.g. <code className="bg-muted px-1 rounded text-[11px] font-mono">https://acme.okta.com</code> (or <code className="bg-muted px-1 rounded text-[11px] font-mono">acme.oktapreview.com</code> for a preview org).
              </p>
              <Input
                id="okta-org-url"
                value={connectOrgUrl}
                onChange={e => setConnectOrgUrl(e.target.value)}
                placeholder="https://acme.okta.com"
                className="mt-1 font-mono"
              />
            </div>
            <div>
              <Label htmlFor="okta-token" className="text-sm">API Token</Label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">
                Kestrel authenticates to the Okta Management API with an <strong className="text-foreground">SSWS API token</strong>. Create one in the Okta Admin Console under <strong className="text-foreground">Security → API → Tokens → Create token</strong>. The token inherits the permissions of the admin who creates it.
              </p>
              <Input
                id="okta-token"
                type="password"
                value={connectApiToken}
                onChange={e => setConnectApiToken(e.target.value)}
                placeholder="Okta API token"
                className="mt-1 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Stored encrypted and only used to authenticate requests to the Okta API.
              </p>
            </div>
            <Button onClick={handleConnect} disabled={saving || !connectApiToken || !connectOrgUrl.trim()}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Connect Okta
            </Button>
          </CardContent>
        </Card>
        </>
      )}
    </div>
  );
};

export default OktaIntegration;
